import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIsMaster } from '../../common/useIsMaster';
import { makeHasPermissionSelector, tenantSelector } from '../../selector/authenticationSelector';
import Permission from '../../common/permissions';
import { canReshapeRooms } from './combineRoomsRules';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import SegmentedTabs from '../../components/SegmentedTabs';
import DepositsConfigSection from './DepositsConfigSection';
import ProductsSection from './ProductsSection';
import CatalogueOrderSections from './CatalogueOrderSections';
import ServiceConfigurationSection from './ServiceConfigurationSection';
import IdeaCardSection from './IdeaCardSection';
import PriceEntrySection from './PriceEntrySection';
import SeriesSeasonSection from './SeriesSeasonSection';
import HolidayRegionSection from './HolidayRegionSection';
import ServicesPricingSection from './ServicesPricingSection';
import CheckInCheckOutSection from '../account/CheckInCheckOutSection';
import BookingPageActivationSection from './BookingPageActivationSection';
import BookingPageLegalSection from './BookingPageLegalSection';
import TenantFaqsSection from './TenantFaqsSection';
import { paths } from '../../routes';
import { cn } from '../../utils/utils';
import { useViewedTenantAccount } from '../../hooks/useViewedTenantAccount';
import { RootState } from '../../store/store';

type ServicesTab = 'usecases' | 'payments' | 'pricing' | 'usageflow';

/** Which tab holds each deep-link anchor — same pattern as the Account page:
 *  a section is only visible with its tab selected, so the anchor's tab is
 *  selected first, then the scroll happens. */
const TAB_BY_ANCHOR: Record<string, ServicesTab> = {
    'service-configuration': 'usecases',
    'product-categories': 'usecases',
    'payment-settings': 'payments',
    pricing: 'pricing',
    deposits: 'pricing',
    'check-in-checkout': 'usageflow',
    'legal-documents': 'usageflow',
    faqs: 'usageflow',
    // Linked from the dashboard setup checklist, which used to point at
    // /account#booking-page.
    'booking-page': 'usageflow',
};

/**
 * **Services** — the standing home for per-use-case operational configuration
 * (ADR-0005, amended 2026-08-18: the area is this renamed menu item, not a new
 * nav entry). It replaces "Rental products", which listed groups gated on the
 * SUP service and was read-only except the deposit amount — a resource list
 * wearing the word "products". The route and menu slot are reused; that deposit
 * list is now one section here rather than the whole of it.
 *
 * Tabbed (PO, 2026-08-19, staging review): **Use cases → Payment settings →
 * Pricing**. The per-use-case questions were visually bleeding into the
 * tenant-level payment rules below them; splitting them by what they apply to
 * (one use case vs. all of them vs. money) is the fix. Named "Use cases" to
 * match the Account tab — the relationship is 1:1 today, and "Offers" is
 * reserved for the day one use case grows several sales-side variants
 * (#1808 customer classes).
 *
 * The dividing rule against Account (CEO, 2026-08-17): anything that leads, or
 * could lead, to gating is resolved in Account — bank details, the payment
 * provider, and (phase 2) which use cases a tenant may switch on. Everything
 * else is configured here. That is also the permission argument of ADR-0010:
 * whoever configures offers should not thereby see bank details.
 */
const ServicesPage = () => {
    const { t } = useTranslation();
    const location = useLocation();

    // One resolution of "which tenant am I looking at" — shared with the
    // Account page. A `useGetAccountQuery()` here would always resolve to the
    // CALLER's own tenant, so a master viewing another tenant would configure
    // their own under that tenant's name (#1334, #2059).
    const {
        accountTenant, readOnly, isSelfService, paymentIntegration, tenantKey, ownsViewedTenant, isResolving,
    } = useViewedTenantAccount();
    const isMaster = useIsMaster();
    const selectedTenantSlug = useSelector(tenantSelector);
    // A master viewing another tenant edits that tenant's settings by slug.
    const viewsOtherTenant = isMaster && !ownsViewedTenant;
    const otherTenantSlug = viewsOtherTenant ? selectedTenantSlug ?? undefined : undefined;
    const hasCreateAssets = useSelector(makeHasPermissionSelector(Permission.createAssets()));

    // Use cases for the price view. Self-service: the account's own services
    // list. Legacy tenants have no operational account record, so their list
    // comes from the same store value the sidebar gates on — which is how a
    // legacy SUP tenant sees its price table here.
    const configServices = useSelector(
        (state: RootState) => state.authentication.config?.services,
    );
    const pricingServices = accountTenant?.services ?? configServices ?? [];

    // Legacy tenants have no operational config to edit, so the first two tabs
    // would be empty shells for them — they get the pricing content directly,
    // untabbed, same as this page held for them before.
    const showTabs = isSelfService;
    const [activeTab, setActiveTab] = useState<ServicesTab>('usecases');

    // `?product=<id>` deep link (contract with the object/product detail
    // surface: /rental-products?product=<id>#pricing). The Products section
    // resolves the id and opens the form; here it only forces the pricing
    // tab, so the link works even without the `#pricing` hash.
    const deepLinkProductId = useMemo(
        () => new URLSearchParams(location.search).get('product'),
        [location.search],
    );

    // Select the anchor's (or product deep link's) tab first, then scroll — a
    // section on an unselected tab is hidden, so scrolling without switching
    // would silently do nothing (Account page pattern). ONE effect writes
    // activeTab from the URL: a second writer would race it (useEffect rule).
    useEffect(() => {
        const anchor = location.hash.slice(1);
        const tab = TAB_BY_ANCHOR[anchor];
        if (tab) setActiveTab(tab);
        else if (deepLinkProductId) setActiveTab('pricing');
        if (!anchor) return;
        const timer = setTimeout(() => {
            document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        return () => clearTimeout(timer);
    }, [location.hash, deepLinkProductId]);

    const showPricing = !showTabs || activeTab === 'pricing';
    const showUsageFlow = showTabs && activeTab === 'usageflow';

    return (
        <div className={'px-4 sm:px-6 py-6 max-w-7xl'}>
            <PageHeader>
                <div className={'flex-grow'}>
                    <PageHeadline>{t('services.title')}</PageHeadline>
                    <p className={'text-sm text-foreground/60'}>{t('services.subtitle')}</p>
                </div>
            </PageHeader>

            {/* Services and Account each carry a tab called "Use Cases", and they
                answer different questions: this area configures HOW each use
                case is booked, paid and priced, while WHICH ones a tenant runs
                is chosen in Account. Someone landing here first — Services is
                its own menu entry — finds a tab whose name promises the thing
                they are looking for, so the pointer is stated up front rather
                than left to be discovered (PO, 2026-09-03, Neuland walk-through).

                Whether the selection should eventually live here instead is an
                open question, deliberately not answered by this hint — Account
                has the narrower audience, and today both are owner-level so it
                is the same people either way (collector #2050). */}
            <p className={'mb-6 rounded-[5px] border border-foreground/15 bg-foreground/5'
                + ' px-4 py-3 text-sm text-foreground/70'}
            >
                {t('services.use_case_selection_hint')}{' '}
                <Link
                    to={`${paths.account}#use-cases`}
                    className={'text-primary underline'}
                >
                    {t('services.use_case_selection_link')}
                </Link>
            </p>

            {showTabs && (
                <SegmentedTabs<ServicesTab>
                    className={'mb-6'}
                    value={activeTab}
                    onChange={setActiveTab}
                    items={[
                        // Order (PO, 2026-09-16): the offer before the money.
                        // Which use cases run, then what they contain and cost,
                        // then how that is paid, then what happens after
                        // booking — so the tenant configures WHAT is sold
                        // before HOW it is settled. Payment settings sat second
                        // before; it asked how to charge for products that had
                        // not been described yet.
                        { key: 'usecases', label: t('services.tab_use_cases') },
                        { key: 'pricing', label: t('services.tab_pricing') },
                        { key: 'payments', label: t('services.tab_payment_settings') },
                        { key: 'usageflow', label: t('services.tab_usage_flow') },
                    ]}
                />
            )}

            <div className={'flex flex-col gap-6 max-w-2xl'}>
                {/* `readOnly` is true until the account has answered, so without
                    the `isResolving` check an owner opening this page directly
                    was first told they only have read access. */}
                {readOnly && isSelfService && !isResolving && (
                    <div className={'rounded-[5px] border border-foreground/15 bg-foreground/5'
                        + ' px-4 py-3 text-sm text-foreground/70'}
                    >
                        {t('services.read_only_notice')}
                    </div>
                )}

                {/* The three questions live on the operational tenant record, so
                    they are offered only where that record exists. Legacy
                    tenants (their configuration is bundled per tenant, not in
                    `tenant.config`) keep the pricing sections alone — same as
                    what this page held for them before.

                    Both configuration sections share ONE form state and save
                    payload, so the component stays mounted across tab switches
                    and hides the inactive part itself (see its props). */}
                {isSelfService && (
                    <ServiceConfigurationSection
                        readOnly={readOnly}
                        payments={accountTenant?.payments ?? null}
                        services={accountTenant?.services ?? null}
                        integration={paymentIntegration}
                        hasBankDetails={!!accountTenant?.bank?.iban}
                        invoiceIssuer={accountTenant?.invoiceIssuer}
                        checkInCheckOutFlow={accountTenant?.checkInCheckOutFlow}
                        bookingPage={{
                            slug: accountTenant?.slug ?? '',
                            active: accountTenant?.endUserAppActive ?? false,
                        }}
                        tenantKey={tenantKey}
                        showUseCases={activeTab === 'usecases'}
                        showPaymentSettings={activeTab === 'payments'}
                    />
                )}

                {/* The series season (series booking, step 8): where organisation
                    owners may book series. Same master-by-slug and keying as
                    the invoice issuer, and rendered only once the tenant is
                    loaded for the same reason. */}
                {isSelfService && accountTenant && (
                    <div className={cn(activeTab !== 'usecases' && 'hidden')}>
                        <SeriesSeasonSection
                            key={`series-season-${tenantKey}`}
                            readOnly={readOnly && !viewsOtherTenant}
                            seriesSeason={accountTenant.seriesSeason ?? null}
                            services={accountTenant.services ?? null}
                            masterSlug={otherTenantSlug}
                        />
                    </div>
                )}

                {/* The holiday region (M9, #3041): which state's holidays a
                    series marks and skips. Next to the series season, keyed
                    and gated the same way. */}
                {isSelfService && accountTenant && (
                    <div className={cn(activeTab !== 'usecases' && 'hidden')}>
                        <HolidayRegionSection
                            key={`holiday-region-${tenantKey}`}
                            readOnly={readOnly && !viewsOtherTenant}
                            holidayRegion={accountTenant.holidayRegion ?? null}
                            services={accountTenant.services ?? null}
                            masterSlug={otherTenantSlug}
                        />
                    </div>
                )}

                {/* The end-user "ideas" card — on the Use-cases tab because it
                    appears on the home screen right next to the use-case tiles
                    configured above. Kept mounted and hidden like the sections
                    above so unsaved edits survive a tab switch; keyed on the
                    tenant so a switch resets the form instead of leaking state. */}
                {isSelfService && (
                    <div className={cn(activeTab !== 'usecases' && 'hidden')}>
                        <IdeaCardSection
                            key={`idea-card-${tenantKey}`}
                            readOnly={readOnly}
                            moreToCome={accountTenant?.moreToCome ?? null}
                        />
                    </div>
                )}

                {/* Usage-flow tab: the check-in/check-out flow, per use case.
                    It moved here from Account → Experience because it IS
                    per-use-case operational configuration (ADR-0005 amendment).
                    Booking-page activation joined it on 2026-09-12: switching
                    the end-user app ON and configuring what it does are the same
                    question, and having them one menu item apart was confusing
                    (PO). Branding has not followed yet — see
                    BookingPageActivationSection for why. Its own tab rather than
                    inside the questions above: the sequence there is about
                    commerce — how an offer is booked, whether it costs, how it
                    is paid — while this is about usage after booking, and it is
                    bulky (six step toggles plus four bilingual texts per use
                    case). Kept mounted and hidden like the others so unsaved
                    edits survive a tab switch. */}
                <div className={cn(!showUsageFlow && 'hidden')}>
                    {/* The slug is passed for the same reason this page uses
                        `useViewedTenantAccount` above: the section resolves its
                        own account otherwise, so a master would configure their
                        OWN tenant under another tenant's name (#1334, #2059). */}
                    <div className={'flex flex-col gap-6'}>
                        {/* The on-switch first: whether the app is live is the
                            fact that makes everything below it matter. */}
                        <BookingPageActivationSection
                            readOnly={readOnly}
                            slug={accountTenant?.slug ?? ''}
                            active={accountTenant?.endUserAppActive ?? false}
                        />

                        {/* What the live page tells customers they are agreeing
                            to. Derived from the use cases, their charging mode
                            and the tenant record — except the tenant's own AGB,
                            the one thing set here (ADR-0005, 2026-10-02). */}
                        <BookingPageLegalSection
                            // Own-terms drafts belong to one tenant (#909/#929).
                            key={`legal-documents-${tenantKey}`}
                            organizationName={accountTenant?.organizationName}
                            services={accountTenant?.services ?? null}
                            payments={accountTenant?.payments ?? null}
                            website={accountTenant?.contact?.website ?? null}
                            ownTerms={accountTenant?.ownTerms}
                            // A master has the owner's rights here (PO, 2026-10-30):
                            // on another tenant it writes by slug, like the series season.
                            readOnly={readOnly && !viewsOtherTenant}
                            masterSlug={otherTenantSlug}
                        />

                        {/* The tenant's own FAQ questions, after kolula's
                            fixed set (ADR-0003 amendment 2026-10-06). */}
                        <TenantFaqsSection
                            // Open forms belong to one tenant (#909/#929).
                            key={`faqs-${tenantKey}`}
                            faqs={accountTenant?.faqs}
                            services={accountTenant?.services ?? null}
                            readOnly={readOnly}
                        />

                        <CheckInCheckOutSection
                            // An open draft belongs to one tenant (#909/#929).
                            key={`checkin-${tenantKey}`}
                            readOnly={readOnly}
                            services={accountTenant?.services ?? null}
                            {...(isMaster && selectedTenantSlug
                                ? { masterSlug: selectedTenantSlug }
                                : {})}
                        />
                    </div>
                </div>

                {/* Pricing tab: the price structure per use case (phase 1b/3)
                    plus the deposits — both money, kept together. Hidden via
                    CSS rather than unmounted so an open editor dialog's state
                    survives an accidental tab switch. */}
                <div className={cn('flex flex-col gap-6', !showPricing && 'hidden')}>
                    {/* The products themselves (create/edit) come first: the
                        price structure below renders what they cost. */}
                    <ProductsSection
                        services={pricingServices}
                        readOnly={readOnly}
                        payments={accountTenant?.payments ?? null}
                        deepLinkProductId={deepLinkProductId}
                    />

                    {/* The order the booking page shows categories, products
                        and objects in (#3016), right after the products it
                        arranges. Keyed per tenant: an open order belongs to
                        one tenant (#909/#929). */}
                    <CatalogueOrderSections
                        key={`manual-order-${tenantKey}`}
                        readOnly={readOnly}
                        isSelfService={isSelfService}
                    />

                    {/* How prices are typed, at the head of the price list it
                        governs (#2967; PO, 2026-10-21). Stored on the operational
                        tenant record, so self-service only; hidden for a
                        Kleinunternehmer, where net and gross are the same. */}
                    {isSelfService && accountTenant && !accountTenant.isKleinunternehmer && (
                        <PriceEntrySection
                            key={`price-entry-${tenantKey}`}
                            readOnly={readOnly && !viewsOtherTenant}
                            priceEntry={accountTenant.priceEntry === 'net' ? 'net' : 'gross'}
                            masterSlug={otherTenantSlug}
                        />
                    )}

                    {pricingServices.length > 0 && (
                        <ServicesPricingSection
                            services={pricingServices}
                            payments={accountTenant?.payments ?? null}
                            readOnly={readOnly}
                            canReshapeRooms={canReshapeRooms({ readOnly, isMaster, hasCreateAssets })}
                        />
                    )}

                    <DepositsConfigSection />
                </div>
            </div>
        </div>
    );
};

export default ServicesPage;
